import styled from '@emotion/styled';
import { ObjectIdSchema } from '@racemap/sdk/schema/base';
import { RacemapColors } from '@racemap/utilities/consts/common';
import { Splitter } from 'antd';
import { type FC, useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '../../lib/customHooks';
import { useStore } from '../../store/reducers';
import { FilterAndTagsColumn } from './Layout/FilterAndTagsBar';
import { MapAndInfosColumn } from './Layout/MapAndInfosColumn';
import { TrackerTableColumn } from './Layout/TrackerTableColumn';

export const TrackerManagerDesktop: FC = () => {
  const inspectedTrackerId = useStore((s) => s.trackers.inspectedTrackerId);
  const { changeInspectedTrackerId } = useStore((s) => s.trackers.actions);
  const query = useQuery();
  const navigate = useNavigate();

  const [filterTreePanelWidth, setFilterTreePanelWidth] = useState(20);
  const [trackerInfoPanelWidth, setTrackerInfoPanelWidth] = useState(25);

  const handleLeftToggle = useCallback(() => {
    setFilterTreePanelWidth((prev) => (prev === 0 ? 20 : 0));
  }, []);

  const handleRightToggle = useCallback(() => {
    setTrackerInfoPanelWidth((prev) => (prev === 0 ? 25 : 0));
  }, []);

  useEffect(() => {
    const result = ObjectIdSchema.safeParse(query.get('inspectedTracker') || '');
    if (result.success) {
      changeInspectedTrackerId(result.data);
    } else {
      changeInspectedTrackerId(null);
    }
  }, [query, changeInspectedTrackerId]);

  useEffect(() => {
    if (inspectedTrackerId == null && trackerInfoPanelWidth > 0) {
      setTrackerInfoPanelWidth(0);
    } else if (inspectedTrackerId != null && trackerInfoPanelWidth === 0) {
      setTrackerInfoPanelWidth(25);
    }
  }, [inspectedTrackerId, trackerInfoPanelWidth]);

  const handleCloseInfoColumn = () => {
    if (inspectedTrackerId != null) {
      navigate('/admin/trackers');
    } else {
      handleRightToggle();
    }
  };

  return (
    <Container>
      <Main>
        <Splitter
          onResize={(sizes: Array<number>) => {
            const totalSize = sizes[0] + sizes[1] + sizes[2];
            setFilterTreePanelWidth((sizes[0] / totalSize) * 100);
            setTrackerInfoPanelWidth((sizes[2] / totalSize) * 100);
          }}
        >
          <Splitter.Panel size={`${filterTreePanelWidth}%`} min="15%" max="35%">
            <FilterAndTagsColumn onLeftClose={handleLeftToggle} />
          </Splitter.Panel>
          <Splitter.Panel defaultSize="62%" min="20%" max="70%">
            <TrackerTableColumn
              isLeftClosed={filterTreePanelWidth === 0}
              onLeftOpen={handleLeftToggle}
            />
          </Splitter.Panel>
          <Splitter.Panel size={`${trackerInfoPanelWidth}%`} min="20%" max="50%">
            <MapAndInfosColumn onCloseClick={handleCloseInfoColumn} />
          </Splitter.Panel>
        </Splitter>
      </Main>
    </Container>
  );
};

const Container = styled.div`
  height: 100%;
  width: 100%;
  position: relative;
`;

const Main = styled.div`
  margin: 10px;
  height: calc(100% - 20px);
  position: relative;
  bottom: 0;
  right: 0;
  left: 0;
  background: white;
  border: solid 1px ${RacemapColors.LightGray};
  border-radius: 5px;
`;
